import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { OrderCancelProvider } from "./OrderCancelContext";
import { getDisplayStatus } from "../../utils/statusMapping";

const OrdersContext = createContext();

export const useOrders = () => useContext(OrdersContext);

export const OrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add ref to track processed orders and prevent duplicates
  const processedOrders = useRef(new Set());
  const orderPlacementInProgress = useRef(false);

  // Helper function to consolidate timeline steps with customer-friendly statuses
  const consolidateTimeline = (statusHistories, order) => {
    if (!statusHistories || !Array.isArray(statusHistories) || statusHistories.length === 0) {
      // Fallback to old audit fields for backward compatibility
      const fallbackTimeline = [
        order.placed_audit_time && {
          status: "Placed",
          description: "Order Placed",
          date: order.placed_audit_time.split(" ")[0],
          time: order.placed_audit_time.split(" ")[1],
        },
        (order.processing_audit_time || order.packaging_audit_time) && {
          status: "Awaiting Process",
          description: "Awaiting Process",
          date: (order.processing_audit_time || order.packaging_audit_time).split(" ")[0],
          time: (order.processing_audit_time || order.packaging_audit_time).split(" ")[1],
        },
        order.shipped_audit_time && {
          status: "Shipped",
          description: "Shipped",
          date: order.shipped_audit_time.split(" ")[0],
          time: order.shipped_audit_time.split(" ")[1],
        },
        order.delivered_audit_time && {
          status: "Delivered",
          description: "Delivered",
          date: order.delivered_audit_time.split(" ")[0],
          time: order.delivered_audit_time.split(" ")[1],
        },
        order.returned_audit_time && {
          status: "Return Requested",
          description: "Return Requested",
          date: order.returned_audit_time.split(" ")[0],
          time: order.returned_audit_time.split(" ")[1],
        },
        order.cancel_requested_audit_time && {
          status: "Cancel Requested",
          description: "Cancel Requested",
          date: order.cancel_requested_audit_time.split(" ")[0],
          time: order.cancel_requested_audit_time.split(" ")[1],
        },
        order.cancelled_audit_time && {
          status: "Cancelled",
          description: "Cancelled",
          date: order.cancelled_audit_time.split(" ")[0],
          time: order.cancelled_audit_time.split(" ")[1],
        },
        order.rejected_audit_time && {
          status: "Rejected",
          description: "Rejected",
          date: order.rejected_audit_time.split(" ")[0],
          time: order.rejected_audit_time.split(" ")[1],
        },
        order.refund_audit_time && {
          status: "Refunded",
          description: "Refunded",
          date: order.refund_audit_time.split(" ")[0],
          time: order.refund_audit_time.split(" ")[1],
        },
      ].filter(Boolean);

      return fallbackTimeline;
    }

    // Process status histories and consolidate intermediate steps
    const consolidatedTimeline = [];
    let hasIntermediateStep = false;
    let intermediateStepData = null;

    // Define intermediate processing statuses that should be consolidated
    const intermediateStatuses = [
      'processing',
      'printing_started',
      'production_completed',
      'quality_check',
      'packaging'
    ];

    // Sort status histories by date/time to ensure proper order
    const sortedHistories = [...statusHistories].sort((a, b) => {
      const dateA = new Date(a.create_audit_time || '');
      const dateB = new Date(b.create_audit_time || '');
      return dateA - dateB;
    });

    // Filter out return_received status entries and track the last valid status
    const filteredHistories = [];
    let lastValidStatus = null;
    let returnReceivedFound = false;

    for (let i = 0; i < sortedHistories.length; i++) {
      const history = sortedHistories[i];
      const status = history.status?.toLowerCase();

      // Skip return_received status
      if (status === 'return_received') {
        returnReceivedFound = true;
        continue;
      }

      filteredHistories.push(history);
      lastValidStatus = history;
    }

    for (const history of filteredHistories) {
      const auditTime = history.create_audit_time || '';
      const [date, time] = auditTime.split(' ');
      const status = history.status?.toLowerCase();

      if (!date || !status) continue;

      // Handle different status types with customer-friendly display
      if (status === 'placed') {
        consolidatedTimeline.push({
          status: 'Placed',
          description: 'Order Placed',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else if (intermediateStatuses.includes(status)) {
        // Consolidate all intermediate steps into one "Awaiting Process" step
        if (!hasIntermediateStep) {
          intermediateStepData = {
            status: 'Awaiting Process',
            description: 'Awaiting Process',
            date,
            time: time || '',
            content: history.content || null,
            shipping_details: history.shipping_details || null
          };
          hasIntermediateStep = true;
        }
        // Update the time to the latest intermediate step time
        else if (intermediateStepData) {
          intermediateStepData.date = date;
          intermediateStepData.time = time || '';
          // Keep the latest content and shipping details
          if (history.content) {
            intermediateStepData.content = history.content;
          }
          if (history.shipping_details) {
            intermediateStepData.shipping_details = history.shipping_details;
          }
        }
      } else if (status === 'shipped') {
        // Add the consolidated intermediate step before shipped (if it exists)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        consolidatedTimeline.push({
          status: 'Shipped',
          description: 'Shipped',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else if (status === 'delivered') {
        // Add the consolidated intermediate step before delivered (if it exists and not already added)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        consolidatedTimeline.push({
          status: 'Delivered',
          description: 'Delivered',
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      } else {
        // Add the consolidated intermediate step before other statuses (if it exists and not already added)
        if (hasIntermediateStep && intermediateStepData) {
          consolidatedTimeline.push(intermediateStepData);
          hasIntermediateStep = false;
          intermediateStepData = null;
        }

        // Handle other statuses with customer-friendly display
        const getStatusInfo = (status) => {
          switch (status) {
            case 'returned':
            case 'return_requested':
              return { status: 'Return Requested', description: 'Return Requested' };
            case 'return_approved':
              return { status: 'Return Approved', description: 'Return Approved' };
            case 'return_rejected':
              return { status: 'Return Rejected', description: 'Return Rejected' };
            case 'cancel_requested':
            case 'cancellation_requested':
              return { status: 'Cancel Requested', description: 'Cancel Requested' };
            case 'cancelled':
              return { status: 'Cancelled', description: 'Cancelled' };
            case 'rejected':
              return { status: 'Rejected', description: 'Rejected' };
            case 'refund_processing':
              return { status: 'Refund Processing', description: 'Refund Processing' };
            case 'refund':
            case 'refunded':
              return { status: 'Refunded', description: 'Refunded' };
            case 'inspection_pending':
              return { status: 'Inspection Pending', description: 'Inspection Pending' };
            case 'partial_refund':
              return { status: 'Partial Refund', description: 'Partial Refund' };
            case 'replacement_processing':
              return { status: 'Replacement Processing', description: 'Replacement Processing' };
            case 'returned_no_refund':
              return { status: 'Returned (No Refund)', description: 'Returned (No Refund)' };
            default:
              return { status: getDisplayStatus(status), description: getDisplayStatus(status) };
          }
        };

        const statusInfo = getStatusInfo(status);
        consolidatedTimeline.push({
          ...statusInfo,
          date,
          time: time || '',
          content: history.content || null,
          shipping_details: history.shipping_details || null
        });
      }
    }

    // If we have an intermediate step that wasn't added (order is still in processing), add it
    if (hasIntermediateStep && intermediateStepData) {
      consolidatedTimeline.push(intermediateStepData);
    }

    return consolidatedTimeline;
  };

  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (!token || !user) {
      setError("Please login to view your orders.");
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userData = JSON.parse(user);
      const userId = userData?.id;

      if (!userId) {
        throw new Error("User ID not found. Please login again.");
      }

      const res = await axios.get(`${BASE_URL}/orders/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 10000,
      });

      if (res.data.status !== "200") {
        throw new Error("Failed to fetch orders. Invalid response from server.");
      }

      // Map API response to your order format with proper null checks
      const formattedOrders = res.data.data
        .map((order) => {
          try {
            // Process all items, including those with null products
            const allItems = order.items || [];
            const validItems = [];
            const invalidItems = [];

            allItems.forEach((item) => {
              if (item.product !== null && item.product !== undefined) {
                try {
                  // Parse product images safely
                  let productImages = [];
                  try {
                    productImages = JSON.parse(
                      item.product.product_images || "[]",
                    );
                  } catch (parseError) {
                    productImages = [];
                  }

                  // Get the first image or use a default
                  const firstImage =
                    productImages.length > 0
                      ? productImages[0]
                      : "default-product.jpg";
                  const imageUrl = `${CDN_URL.replace(/\/$/, "")}/${firstImage}`;

                  // Process customization banners
                  let customisedBanners = [];
                  try {
                    if (item.product.customised_banners) {
                      customisedBanners = Array.isArray(item.product.customised_banners)
                        ? item.product.customised_banners.map(banner => ({
                            image: banner.image ? `${CDN_URL.replace(/\/$/, "")}/${banner.image}` : null
                          })).filter(banner => banner.image)
                        : [];
                    }
                  } catch (bannerError) {
                    customisedBanners = [];
                  }

                  validItems.push({
                    id: item.product.id,
                    orderItemId: item.id,
                    name: item.product.product_name || "Unknown Product",
                    category: item.product.category_name || "Unknown Category",
                    price: parseFloat(item.price) || 0,
                    quantity: parseInt(item.quantity) || 1,
                    image: imageUrl,
                    customization: {},
                    // Add customization fields
                    customisations: item.product.customisations || "No",
                    isCustomizable: item.product.customisations === "Yes",
                    customisedBanners: customisedBanners,
                    // Add return status field
                    returnStatus: item.product.return_status || null,
                  });
                } catch (itemError) {
                  invalidItems.push(item);
                }
              } else {
                // Handle items with null products (unavailable items)
                invalidItems.push({
                  id: item.product_id || item.id,
                  orderItemId: item.id,
                  name: "Product No Longer Available",
                  category: "Unknown Category",
                  price: parseFloat(item.price) || 0,
                  quantity: parseInt(item.quantity) || 1,
                  image: "/default-product.jpg",
                  customization: {},
                  isUnavailable: true,
                  customisations: "No",
                  isCustomizable: false,
                  customisedBanners: [],
                  returnStatus: null,
                });
              }
            });

            // Include orders even if they have some invalid items
            const displayItems =
              validItems.length > 0 ? validItems : invalidItems;

            if (displayItems.length === 0) {
              return null;
            }

            // Use the new consolidated timeline function
            const timeline = consolidateTimeline(order.status_histories, order);

            // Determine the effective order status using display status mapping
            let effectiveOrderStatus = getDisplayStatus(order.order_status || "Unknown");

            if (order.status_histories && order.status_histories.length > 0) {
              // Check if the last status in the original history was return_received
              const lastOriginalStatus = order.status_histories[order.status_histories.length - 1];
              if (lastOriginalStatus.status?.toLowerCase() === 'return_received') {
                // Find the previous valid status
                for (let i = order.status_histories.length - 2; i >= 0; i--) {
                  const prevStatus = order.status_histories[i];
                  if (prevStatus.status?.toLowerCase() !== 'return_received') {
                    effectiveOrderStatus = getDisplayStatus(prevStatus.status);
                    break;
                  }
                }
              } else {
                effectiveOrderStatus = getDisplayStatus(lastOriginalStatus.status);
              }
            }

            // Extract promo code information
            const promoCode = order.promo_code || null;
            const promoDiscount = order.promo_value ? parseFloat(order.promo_value) : 0;

            // Get order date from status_histories or fallback to audit fields
            let orderDate = new Date().toISOString().split("T")[0];
            if (order.status_histories && order.status_histories.length > 0) {
              const placedHistory = order.status_histories.find(h => h.status?.toLowerCase() === 'placed');
              if (placedHistory && placedHistory.create_audit_time) {
                orderDate = placedHistory.create_audit_time.split(" ")[0];
              }
            } else if (order.placed_audit_time) {
              orderDate = order.placed_audit_time.split(" ")[0];
            }

            // Get delivery date from status_histories or fallback to audit fields
            let deliveryDate = null;
            if (order.status_histories && order.status_histories.length > 0) {
              const deliveredHistory = order.status_histories.find(h => h.status?.toLowerCase() === 'delivered');
              if (deliveredHistory && deliveredHistory.create_audit_time) {
                deliveryDate = deliveredHistory.create_audit_time.split(" ")[0];
              }
            } else if (order.delivered_audit_time) {
              deliveryDate = order.delivered_audit_time.split(" ")[0];
            }

            return {
              id: order.id,
              orderNumber: order.order_id || `ORDER-${order.id}`,
              date: orderDate,
              status: effectiveOrderStatus,
              total: parseFloat(order.total) || 0,
              subtotal: parseFloat(order.subtotal) || 0,
              shippingCharges: parseFloat(order.shipping_charges) || 0,
              tax: parseFloat(order.tax) || 0,
              promoCode: promoCode,
              promoDiscount: promoDiscount,
              itemCount: displayItems.length,
              estimatedDelivery: deliveryDate,
              trackingNumber: order.transaction_id || null,
              items: displayItems,
              billingAddress: order.billing_address ? {
                firstName: order.billing_address.first_name || "",
                lastName: order.billing_address.last_name || "",
                email: order.billing_address.email || "",
                phone: order.billing_address.phone || "",
                address: order.billing_address.address || "",
                city: order.billing_address.city || "",
                state: order.billing_address.state || "",
                pincode: order.billing_address.pincode || "",
              } : null,
              shippingAddress: {
                name: order.address?.address || "No address",
                street: order.address?.street || "",
                city: order.address?.city || "",
                state: order.address?.state || "",
                zipCode: order.address?.zipcode || "",
              },
              timeline: timeline,
              paymentMethod: order.payment_method || "Unknown",
              paymentStatus: order.payment_status || "Unknown",
              hasUnavailableItems: invalidItems.length > 0,
              reviewStatus: order.review_status || false,
            };
          } catch (orderError) {
            return null;
          }
        })
        .filter((order) => order !== null);

      setOrders(formattedOrders);
      setError(null);

    } catch (err) {
      let errorMessage = "Failed to fetch orders. Please try again.";

      if (err.response?.status === 401) {
        errorMessage = "Session expired. Please login again.";
        setOrders([]);
      } else if (err.response?.status === 404) {
        errorMessage = "No orders found for this user.";
        setOrders([]);
      } else if (err.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please check your connection and try again.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh orders function that can be called externally
  const refreshOrders = useCallback(async () => {
    await fetchOrders();
  }, [fetchOrders]);

  // Simple initial fetch on mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const placeOrder = async (orderData) => {
    // Prevent duplicate order placement
    if (orderPlacementInProgress.current) {
      throw new Error('Order placement already in progress');
    }

    // Check for duplicate order using order identifier
    if (orderData.order_identifier && processedOrders.current.has(orderData.order_identifier)) {
      throw new Error('Duplicate order detected');
    }

    if (!orderData.address_id) {
      throw new Error("Address not selected");
    }

    if (!orderData.shipping_method_id) {
      throw new Error("Shipping method not selected");
    }

    if (!orderData.payment_method) {
      throw new Error("Payment method missing");
    }

    // Mark order placement as in progress
    orderPlacementInProgress.current = true;

    // Mark this order as processed (but only after successful placement)
    // Don't mark as processed immediately to allow retry on failure
    let orderIdentifierToMark = null;
    if (orderData.order_identifier) {
      orderIdentifierToMark = orderData.order_identifier;
    }

    try {
      // Ensure promo discount is properly applied to the total before sending to API
      const promoDiscount = orderData.promo_discount || 0;

      // If total is already calculated (from BillingSection), use it
      // Otherwise, calculate it from products with promo discount applied
      if (!orderData.total && orderData.products) {
        const productsTotal = orderData.products.reduce((sum, product) => {
          return sum + (product.price * product.quantity);
        }, 0);

        const shippingCharges = orderData.shipping_charges || 0;
        const tax = orderData.tax || 0;

        // Apply promo discount to subtotal + shipping, then add tax
        const subtotalWithShipping = productsTotal + shippingCharges;
        const discountedSubtotal = Math.max(0, subtotalWithShipping - promoDiscount);
        orderData.total = discountedSubtotal + tax;
        orderData.subtotal = discountedSubtotal;
      }

      // Map products to include variation_id for API compatibility
      const mappedProducts = orderData.products.map(item => {
        // Get the correct product_id and ensure it's an integer
        const rawProductId = item.isVariation ? (item.baseProductId || item.product_id) : item.product_id;

        // Handle variation ID extraction from composite IDs like "58_var_6"
        let productId;
        if (item.isVariation && typeof rawProductId === 'string' && rawProductId.includes('_var_')) {
          // Extract base product ID from variation ID format "58_var_6"
          const baseId = rawProductId.split('_var_')[0];
          productId = parseInt(baseId, 10);
        } else {
          productId = parseInt(rawProductId, 10);
        }

        // Validate that product_id is a valid integer
        if (isNaN(productId)) {
          throw new Error(`Invalid product ID: ${rawProductId}`);
        }

        const productData = {
          product_id: productId,
          quantity: parseInt(item.quantity, 10) || 1,
          price: parseFloat(item.price) || 0
        };

        // Add variation_id if this is a variation product
        if (item.isVariation && item.variationId) {
          productData.variation_id = String(item.variationId);
        } else {
          // For main products, set variation_id to null
          productData.variation_id = null;
        }

        return productData;
      });

      // Ensure the promo discount is properly reflected in the final order data
      const finalOrderData = {
        ...orderData,
        // Update products array with proper variation_id mapping
        products: mappedProducts,
        // Make sure promo_discount is included as promo_value for API compatibility
        promo_value: promoDiscount,
        // Ensure total reflects the discount
        total: orderData.total || 0,
        subtotal: orderData.subtotal || 0
      };

      const response = await axios.post(`${BASE_URL}/place-orders`, finalOrderData);

      // Mark as processed only after successful placement
      if (orderIdentifierToMark) {
        processedOrders.current.add(orderIdentifierToMark);
      }

      // After successful order placement, refresh orders with a delay
      setTimeout(() => {
        refreshOrders();
      }, 1000);

      return response.data;
    } catch (error) {
      // Don't mark as processed if placement failed - this allows retry
      // The order identifier was not added to processedOrders yet, so no need to remove
      throw error;
    } finally {
      // Reset order placement flag to allow retry
      orderPlacementInProgress.current = false;
    }
  };

  return (
    <OrderCancelProvider>
      <OrdersContext.Provider value={{
        orders,
        loading,
        error,
        placeOrder,
        refreshOrders
      }}>
        {children}
      </OrdersContext.Provider>
    </OrderCancelProvider>
  );
};
